import { useSelector } from '@xstate5/react';

import { type LocationSearchService } from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derived state flags bound to a `LocationSearchService` actor ref.
 *
 * Each flag is its own `useSelector` so a component only re-renders when the
 * specific flag it reads changes.
 */
export const useLocationSearchFlags = (params: Params) => {
  const { locationSearchActorRef } = params;

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoadingPredictions = useSelector(locationSearchActorRef, (snapshot) =>
    snapshot.matches({ search: { loading: 'searchDeliveryAddresses' } }),
  );

  const isLoadingLocations = useSelector(
    locationSearchActorRef,
    (snapshot) =>
      (snapshot.matches({ search: 'loading' }) &&
        !snapshot.matches({ search: { loading: 'searchDeliveryAddresses' } })) ||
      snapshot.matches({ searchForPredictions: 'selecting' }),
  );

  const isAddingAddress = useSelector(locationSearchActorRef, (snapshot) =>
    snapshot.matches({ search: { loading: 'addNewDeliveryAddress' } }),
  );

  const isLoadingRecentAndNearbyLocations = useSelector(
    locationSearchActorRef,
    (snapshot) =>
      snapshot.matches({ search: { setup: 'setupRecentAndNearbyLocations' } }) ||
      snapshot.matches({ search: { setup: 'setupNearbyLocations' } }),
  );

  const isUsingSearchField = useSelector(locationSearchActorRef, (snapshot) =>
    snapshot.matches({ searchForPredictions: 'searching' }),
  );

  const shouldShowLocationWarning = useSelector(
    locationSearchActorRef,
    (snapshot) =>
      (snapshot.matches({ search: { warning: 'pickupLocation' } }) ||
        snapshot.matches({ search: { warning: 'outpostLocation' } }) ||
        snapshot.matches({ search: { recentAndNearbyLocations: { warning: 'pickupLocation' } } }) ||
        snapshot.matches({
          search: { recentAndNearbyLocations: { warning: 'outpostLocation' } },
        })) &&
      snapshot.context.activeLocation !== undefined,
  );

  const shouldRenderRecentAndNearbyLocations = useSelector(locationSearchActorRef, (snapshot) => {
    const isLoadingRecentAndNearby =
      snapshot.matches({ search: { setup: 'setupRecentAndNearbyLocations' } }) ||
      snapshot.matches({ search: { setup: 'setupNearbyLocations' } });

    return (
      !snapshot.matches({ searchType: 'delivery' }) &&
      (snapshot.matches({ search: 'recentAndNearbyLocations' }) || isLoadingRecentAndNearby)
    );
  });

  const shouldRenderDeliveryAddressForm = useSelector(locationSearchActorRef, (snapshot) =>
    snapshot.matches({ search: 'showingDeliveryAddressForm' }),
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    isLoadingPredictions,
    isLoadingLocations,
    isAddingAddress,
    isLoadingRecentAndNearbyLocations,
    isUsingSearchField,
    shouldShowLocationWarning,
    shouldRenderRecentAndNearbyLocations,
    shouldRenderDeliveryAddressForm,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  locationSearchActorRef: LocationSearchService;
};
